import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { caseService, supabase, CaseRecord } from "../lib/supabase";

export interface CaseHistory {
  id: string;
  userId: string;
  createdAt: string;
  status: "completed" | "processing" | "error";
  summary: {
    vehicleInfo?: string;
    location?: string;
    documentsCount: number;
    imagesCount: number;
    faultPercentage?: number;
    estimatedCost?: number;
  };
  results?: any;
}

interface CaseHistoryContextType {
  cases: CaseHistory[];
  loading: boolean;
  error: string | null;
  addCase: (
    caseData: Omit<CaseHistory, "id" | "userId" | "createdAt">
  ) => Promise<string>;
  updateCase: (id: string, updates: Partial<CaseHistory>) => Promise<void>;
  deleteCase: (id: string) => Promise<void>;
  getCase: (id: string) => CaseHistory | undefined;
  refreshCases: () => Promise<void>;
  clearHistory: () => Promise<void>;
}

const CaseHistoryContext = createContext<CaseHistoryContextType | undefined>(
  undefined
);

// Local storage key for fallback
const LOCAL_STORAGE_KEY = "insura_cases";

// Helper to transform database record to CaseHistory
function dbToCaseHistory(record: CaseRecord): CaseHistory {
  return {
    id: record.id,
    userId: record.user_id,
    createdAt: record.created_at,
    status: record.status,
    summary: {
      vehicleInfo: record.vehicle_info,
      location: record.location,
      documentsCount: record.documents_count || 0,
      imagesCount: record.images_count || 0,
      faultPercentage: record.fault_percentage,
      estimatedCost: record.estimated_cost,
    },
    results: record.results,
  };
}

// Helper to transform CaseHistory to database record
function caseHistoryToDb(caseData: CaseHistory, userEmail?: string): Partial<CaseRecord> {
  return {
    id: caseData.id,
    user_id: caseData.userId,
    user_email: userEmail,
    status: caseData.status,
    vehicle_info: caseData.summary?.vehicleInfo,
    location: caseData.summary?.location,
    documents_count: caseData.summary?.documentsCount || 0,
    images_count: caseData.summary?.imagesCount || 0,
    fault_percentage: caseData.summary?.faultPercentage,
    estimated_cost: caseData.summary?.estimatedCost,
    results: caseData.results,
  };
}

export function CaseHistoryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [cases, setCases] = useState<CaseHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if Supabase is available
  const useSupabase = !!supabase;

  // Load from localStorage (fallback)
  const loadFromLocalStorage = useCallback(() => {
    if (!user) return [];
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        const allCases = JSON.parse(stored);
        return allCases.filter((c: CaseHistory) => c.userId === user.id);
      } catch {
        return [];
      }
    }
    return [];
  }, [user]);

  // Save to localStorage (fallback)
  const saveToLocalStorage = useCallback((casesToSave: CaseHistory[]) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(casesToSave));
  }, []);

  // Fetch cases from Supabase or localStorage
  const fetchCases = useCallback(async () => {
    if (!user) {
      setCases([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (useSupabase) {
        const records = await caseService.fetchCases(user.id);
        const transformedCases = records.map(dbToCaseHistory);
        setCases(transformedCases);
        console.log("📋 Loaded cases from Supabase:", transformedCases.length);
      } else {
        // Fallback to localStorage
        const localCases = loadFromLocalStorage();
        setCases(localCases);
        console.log("📋 Loaded cases from localStorage:", localCases.length);
      }
    } catch (err) {
      console.error("Failed to fetch cases:", err);
      setError("Failed to load case history");
      // Fallback to localStorage on error
      const localCases = loadFromLocalStorage();
      setCases(localCases);
    } finally {
      setLoading(false);
    }
  }, [user, useSupabase, loadFromLocalStorage]);

  // Load cases when user changes
  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const addCase = async (
    caseData: Omit<CaseHistory, "id" | "userId" | "createdAt">
  ): Promise<string> => {
    if (!user) return "";

    const newCaseId = `case_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const newCase: CaseHistory = {
      ...caseData,
      id: newCaseId,
      userId: user.id,
      createdAt: new Date().toISOString(),
    };

    // Optimistically update UI
    setCases((prev) => {
      const updated = [newCase, ...prev];
      if (!useSupabase) {
        saveToLocalStorage(updated);
      }
      return updated;
    });

    try {
      if (useSupabase) {
        await caseService.createCase(caseHistoryToDb(newCase, user.email));
        console.log("✅ Case saved to Supabase:", newCaseId);
      }
    } catch (err) {
      console.error("Failed to save case to database:", err);
      // Keep in local state even if save fails
    }

    return newCaseId;
  };

  const updateCase = async (
    id: string,
    updates: Partial<CaseHistory>
  ): Promise<void> => {
    if (!user) return;

    console.log("📝 Updating case:", id, "with status:", updates.status);

    // Optimistically update UI
    setCases((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, ...updates } : c));
      if (!useSupabase) {
        saveToLocalStorage(updated);
      }
      return updated;
    });

    try {
      if (useSupabase) {
        const dbUpdates: Partial<CaseRecord> = {
          status: updates.status,
          vehicle_info: updates.summary?.vehicleInfo,
          location: updates.summary?.location,
          documents_count: updates.summary?.documentsCount,
          images_count: updates.summary?.imagesCount,
          fault_percentage: updates.summary?.faultPercentage,
          estimated_cost: updates.summary?.estimatedCost,
          results: updates.results,
        };
        await caseService.updateCase(id, dbUpdates);
        console.log("✅ Case updated in Supabase:", id);
      }
    } catch (err) {
      console.error("Failed to update case in database:", err);
    }
  };

  const deleteCase = async (id: string): Promise<void> => {
    if (!user) return;

    // Optimistically update UI
    setCases((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      if (!useSupabase) {
        saveToLocalStorage(updated);
      }
      return updated;
    });

    try {
      if (useSupabase) {
        await caseService.deleteCase(id);
        console.log("✅ Case deleted from Supabase:", id);
      }
    } catch (err) {
      console.error("Failed to delete case from database:", err);
      fetchCases(); // Refresh to get actual state
    }
  };

  const getCase = (id: string): CaseHistory | undefined => {
    return cases.find((c) => c.id === id);
  };

  const refreshCases = async (): Promise<void> => {
    await fetchCases();
  };

  const clearHistory = async (): Promise<void> => {
    if (!user) return;

    try {
      if (useSupabase) {
        // Delete all cases one by one
        const deletePromises = cases.map((c) => caseService.deleteCase(c.id));
        await Promise.all(deletePromises);
      }
      setCases([]);
      if (!useSupabase) {
        saveToLocalStorage([]);
      }
      console.log("✅ All cases cleared");
    } catch (err) {
      console.error("Failed to clear history:", err);
      fetchCases();
    }
  };

  return (
    <CaseHistoryContext.Provider
      value={{
        cases,
        loading,
        error,
        addCase,
        updateCase,
        deleteCase,
        getCase,
        refreshCases,
        clearHistory,
      }}
    >
      {children}
    </CaseHistoryContext.Provider>
  );
}

export function useCaseHistory() {
  const context = useContext(CaseHistoryContext);
  if (context === undefined) {
    throw new Error("useCaseHistory must be used within a CaseHistoryProvider");
  }
  return context;
}
